import { activityFlatsOfModels } from '@components/pages/activity-log/services/activity-log-flat.util';
import { BadgeInfo } from '@models/badge.model';
import { PaginatedResponse } from '@services/api/generic-api-client.service';
import { InvoiceClientService } from '@services/api/invoice/invoice-client.service';
import { IInvoiceModel } from '@services/api/invoice/invoice-model.interface';
import { IApiQueryOptions, IOrderOptions } from '@services/api/models/api-query-params.model';
import { LogService } from '@services/util/log.service';
import { ModelLocalizationService } from '@services/formatter/model-localization.service';
import { parseISO } from 'date-fns';
import { catchError, map, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { InvoiceFilter } from '../models/invoice-filter.model';
import { InvoiceRow, PaginatedInvoices } from '../models/invoice-listing.model';

@Injectable({
  providedIn: 'root'
})
export class InvoiceListingService {
  constructor(
    private readonly invoiceClient: InvoiceClientService,
    private readonly log: LogService,
    private readonly modelLocalization: ModelLocalizationService
  ) {}

  /**
   * Get paginated invoices with optional sorting and filtering
   *
   * @param page Current page number (1-based)
   * @param itemsPerPage Number of items per page
   * @param sortField Field to sort by
   * @param sortOrder Sort direction
   * @param filter Optional filters
   * @returns Observable with paginated invoices
   */
  getInvoices(
    page: number = 1,
    itemsPerPage: number = 30,
    sortField: string | null = null,
    sortOrder: 'asc' | 'desc' | null = null,
    filter?: InvoiceFilter
  ): Observable<PaginatedInvoices> {
    this.log.debug('Fetching invoices with params:', { page, itemsPerPage, sortField, sortOrder, filter });

    // Map frontend field names to API field names for sorting
    const fieldMappings: Record<string, string> = {
      customerName: 'customer_user.name',
      invoiceStatus: 'invoiceStatus.id',
      invoiceNumber: 'invoice_number',
      fulfillmentDate: 'fulfillment_date',
      grossTotal: 'gross_total',
      paidStatus: 'paid',
      invoiceDate: 'invoice_date',
      dueDate: 'due_date',
      paymentDate: 'payment_date'
    };

    // Define the order object based on sorting parameters
    let orderOptions: IOrderOptions = { invoice_date: 'desc' }; // Default sorting

    if (sortField && sortOrder) {
      // Use the mapped API field name if available, otherwise use the original
      const apiField = fieldMappings[sortField] || sortField;
      orderOptions = { [apiField]: sortOrder };
      this.log.debug(`Mapping sort field ${sortField} to API field ${apiField}`);
    }

    // Prepare filter objects
    const filterObj: Record<string, any> = {};
    const dateFilterObj: Record<string, any> = {};

    // Apply advanced filter options if provided
    if (filter) {
      // Global OR-search across multiple fields (backend LIKE %term%)
      if (filter.searchTerm) {
        filterObj['search_term'] = filter.searchTerm;
      }

      // Exact match filters
      if (filter.invoiceNumber) {
        filterObj['invoice_number'] = filter.invoiceNumber;
      }

      // Boolean filters on the API require 1 or 0
      if (filter.paid !== undefined) {
        filterObj['paid'] = filter.paid ? 1 : 0;
      }


      // New text-based filters
      if (filter['customer_user.name']) {
        filterObj['customer_user.name'] = filter['customer_user.name'];
      }

      if (filter['customer_user.email']) {
        filterObj['customer_user.email'] = filter['customer_user.email'];
      }

      if (filter['bookings.name']) {
        filterObj['bookings.name'] = filter['bookings.name'];
      }

      if (filter['user_note']) {
        filterObj['user_note'] = filter['user_note'];
      }

      // Relationship ID filters
      if (filter['invoiceStatus.id']) {
        filterObj['invoiceStatus.id'] = filter['invoiceStatus.id'];
      }

      if (filter['currency.id']) {
        filterObj['currency.id'] = filter['currency.id'];
      }

      // Legacy relationship filters (keeping for compatibility)
      if (filter.customerUserId) {
        filterObj['customer_user.id'] = filter.customerUserId;
      }

      if (filter.issuerUserId) {
        filterObj['issuer_user.id'] = filter.issuerUserId;
      }

      if (filter.invoiceStatusId) {
        filterObj['invoiceStatus.id'] = filter.invoiceStatusId;
      }

      if (filter.currencyId) {
        filterObj['currency.id'] = filter.currencyId;
      }

      // Handle date filters using the new date filtering capabilities
      if (filter.invoiceDate) {
        dateFilterObj['invoice_date'] = filter.invoiceDate;
      }

      if (filter.fulfillmentDate) {
        dateFilterObj['fulfillment_date'] = filter.fulfillmentDate;
      }

      if (filter.dueDate) {
        dateFilterObj['due_date'] = filter.dueDate;
      }

      if (filter.paymentDate) {
        dateFilterObj['payment_date'] = filter.paymentDate;
      }

      this.log.debug('Prepared filter options:', { filterObj, dateFilterObj });
    }

    // Build query options for the API using the new date filtering capabilities
    const options: IApiQueryOptions = {
      pagination: {
        page,
        itemsPerPage
      },
      order: orderOptions
    };

    // Only add filters and dateFilters if they have values
    if (Object.keys(filterObj).length > 0) {
      options.filters = filterObj;
    }

    if (Object.keys(dateFilterObj).length > 0) {
      options.dateFilters = dateFilterObj;
    }

    this.log.debug('Applied query options:', options);

    // Use the standard getItems method which now handles date filtering
    const request$ = this.invoiceClient.getItems('', options);

    return request$.pipe(
      map((response: PaginatedResponse<IInvoiceModel>) => {
        // Transform API models to UI models
        const transformedInvoices = response.items.map((invoice) => this.mapToInvoiceRow(invoice));

        return {
          items: transformedInvoices,
          totalCount: response.totalItems || 0,
          page,
          itemsPerPage
        };
      }),
      catchError((error) => {
        this.log.error('Error fetching invoices:', error);
        return of({
          items: [],
          totalCount: 0,
          page,
          itemsPerPage
        });
      })
    );
  }

  /**
   * Delete an invoice
   *
   * @param id ID of the invoice to delete
   * @returns Observable with boolean indicating success
   */
  deleteInvoice(id: number): Observable<boolean> {
    this.log.debug('Deleting invoice with ID:', id);

    return this.invoiceClient.deleteItem(id).pipe(
      map(() => true),
      catchError((error) => {
        this.log.error('Error deleting invoice:', error);
        return of(false);
      })
    );
  }

  /**
   * Map API invoice model to UI invoice row model
   * @param invoice API invoice model
   * @returns UI invoice row model
   */
  private mapToInvoiceRow(invoice: IInvoiceModel): InvoiceRow {
    return {
      id: invoice.id!,
      invoiceNumber: invoice.invoice_number || null,
      customerName:
        invoice.customerUser?.name ||
        invoice.customerBillingAddress?.company_name ||
        invoice.customerBillingAddress?.name ||
        invoice.bookings?.[0]?.name ||
        '',
      invoiceStatus: invoice.invoiceStatus
        ? new BadgeInfo(invoice.invoiceStatus.id!, this.modelLocalization.getInvoiceStatusKey(invoice.invoiceStatus), 'bg-blue-100 text-blue-800', 'pi pi-info-circle')
        : new BadgeInfo(0, 'invoices.noStatus', 'bg-gray-100 text-gray-800', 'pi pi-minus-circle'),
      userNote: invoice.user_note || '',
      fulfillmentDate: invoice.fulfillment_date ? parseISO(invoice.fulfillment_date) : null,
      grossTotal: typeof invoice.gross_total === 'string' ? parseFloat(invoice.gross_total) : invoice.gross_total || 0,
      currency: invoice.currency
        ? {
            '@id': invoice.currency['@id']!,
            id: invoice.currency.id!,
            name: invoice.currency.name || 'Unknown Currency',
            code: invoice.currency.code || 'XXX',
            symbol: invoice.currency.symbol || '?'
          }
        : {
            '@id': '',
            id: 0,
            name: 'Unknown Currency',
            code: 'XXX',
            symbol: '?'
          },
      paidStatus: invoice.paid || false,
      invoiceSource: this.extractInvoiceSource(invoice),
      bookingId: invoice.bookings && invoice.bookings.length > 0 ? invoice.bookings[0].id : undefined,
      bookingFlats: (invoice.bookings ?? []).flatMap((booking) => activityFlatsOfModels(booking?.flats)),
      invoiceDate: invoice.invoice_date ? parseISO(invoice.invoice_date) : null,
      dueDate: invoice.due_date ? parseISO(invoice.due_date) : null,
      paymentDate: invoice.payment_date ? parseISO(invoice.payment_date) : null
    };
  }

  /**
   * Extract invoice source from bookings
   * @param invoice API invoice model
   * @returns Invoice source name or default
   */
  private extractInvoiceSource(invoice: IInvoiceModel): string {
    if (invoice.bookings && invoice.bookings.length > 0) {
      const firstBooking = invoice.bookings[0];
      if (firstBooking.bookingSource?.name) {
        return firstBooking.bookingSource.name;
      }
    }
    return 'N/A';
  }
}
